'use client';

import { useState } from 'react';
import Toggle from '@/components/ui/Toggle';
import SeverityBadge, { Severity } from '@/components/ui/SeverityBadge';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import RuleFormModal from './RuleFormModal';

type RuleType = 'threshold' | 'signature' | 'behavioral' | 'anomaly';

interface AlertRule {
  id: string;
  name: string;
  type: RuleType;
  enabled: boolean;
  severity: Severity;
  condition: string;
  lastTriggered: string;
  triggerCount7d: number;
  fpRate: number;
  mitreId: string;
  mitreTactic: string;
  suppressionWindow: string;
  logSource: string;
}

const initialRules: AlertRule[] = [
  { id: 'RULE-001', name: 'SSH Brute Force Detection', type: 'threshold', enabled: true, severity: 'critical', condition: '>50 failed SSH auths in 60s from same src IP', lastTriggered: '07:18:29', triggerCount7d: 47, fpRate: 4.2, mitreId: 'T1110', mitreTactic: 'Credential Access', suppressionWindow: '5 min', logSource: 'syslog/auth' },
  { id: 'RULE-002', name: 'SQL Injection Pattern Match', type: 'signature', enabled: true, severity: 'high', condition: 'HTTP request body matches SQLI regex patterns', lastTriggered: '07:17:44', triggerCount7d: 312, fpRate: 12.8, mitreId: 'T1190', mitreTactic: 'Initial Access', suppressionWindow: '1 min', logSource: 'waf/access' },
  { id: 'RULE-003', name: 'C2 Beacon Periodic Traffic', type: 'behavioral', enabled: true, severity: 'critical', condition: 'Outbound connection to threat intel IOC with <30s jitter', lastTriggered: '07:04:12', triggerCount7d: 8, fpRate: 1.1, mitreId: 'T1071', mitreTactic: 'Command & Control', suppressionWindow: '0', logSource: 'netflow/egress' },
  { id: 'RULE-004', name: 'DNS Tunneling Entropy', type: 'anomaly', enabled: true, severity: 'critical', condition: 'DNS query entropy >4.5 bits/char over 100 queries', lastTriggered: '05:54:33', triggerCount7d: 3, fpRate: 2.3, mitreId: 'T1048', mitreTactic: 'Exfiltration', suppressionWindow: '10 min', logSource: 'dns/queries' },
  { id: 'RULE-005', name: 'Lateral Movement — SMB', type: 'behavioral', enabled: true, severity: 'critical', condition: 'SMB connections to >5 internal hosts in 120s', lastTriggered: '06:31:09', triggerCount7d: 12, fpRate: 8.4, mitreId: 'T1021', mitreTactic: 'Lateral Movement', suppressionWindow: '2 min', logSource: 'netflow/internal' },
  { id: 'RULE-006', name: 'RDP Brute Force', type: 'threshold', enabled: true, severity: 'high', condition: '>20 failed RDP auths in 30s', lastTriggered: '04:12:08', triggerCount7d: 28, fpRate: 6.7, mitreId: 'T1133', mitreTactic: 'Initial Access', suppressionWindow: '5 min', logSource: 'windows/security' },
  { id: 'RULE-007', name: 'Port Scan Detection', type: 'threshold', enabled: true, severity: 'medium', condition: 'Single src IP contacts >30 dst ports in 60s', lastTriggered: '07:18:14', triggerCount7d: 189, fpRate: 22.1, mitreId: 'T1046', mitreTactic: 'Discovery', suppressionWindow: '15 min', logSource: 'netflow/all' },
  { id: 'RULE-008', name: 'XSS Payload in HTTP', type: 'signature', enabled: true, severity: 'medium', condition: 'HTTP param contains <script> or JS event handlers', lastTriggered: '07:17:31', triggerCount7d: 421, fpRate: 31.4, mitreId: 'T1059', mitreTactic: 'Execution', suppressionWindow: '1 min', logSource: 'waf/access' },
  { id: 'RULE-009', name: 'Privilege Escalation — Linux', type: 'signature', enabled: true, severity: 'high', condition: 'sudo/su followed by setuid binary execution', lastTriggered: '2026-03-15 18:11', triggerCount7d: 7, fpRate: 15.2, mitreId: 'T1068', mitreTactic: 'Privilege Escalation', suppressionWindow: '0', logSource: 'auditd/syscall' },
  { id: 'RULE-010', name: 'Large Data Transfer Outbound', type: 'anomaly', enabled: true, severity: 'high', condition: 'Outbound transfer >500MB to non-whitelisted IP in 1h', lastTriggered: '2026-03-15 14:50', triggerCount7d: 4, fpRate: 25.0, mitreId: 'T1048', mitreTactic: 'Exfiltration', suppressionWindow: '30 min', logSource: 'netflow/egress' },
  { id: 'RULE-011', name: 'Failed Auth Spike — Any Service', type: 'threshold', enabled: false, severity: 'medium', condition: '>100 auth failures across any service in 5 min', lastTriggered: '2026-03-14 09:22', triggerCount7d: 0, fpRate: 38.9, mitreId: 'T1110', mitreTactic: 'Credential Access', suppressionWindow: '10 min', logSource: 'syslog/auth' },
  { id: 'RULE-012', name: 'New Admin Account Created', type: 'signature', enabled: true, severity: 'medium', condition: 'useradd/net user with admin group membership', lastTriggered: '2026-03-15 11:33', triggerCount7d: 2, fpRate: 50.0, mitreId: 'T1136', mitreTactic: 'Persistence', suppressionWindow: '0', logSource: 'auditd/syscall' },
  { id: 'RULE-013', name: 'DoS Traffic Volume', type: 'threshold', enabled: true, severity: 'medium', condition: 'Inbound PPS >100k/s from single source', lastTriggered: '2026-03-15 14:22', triggerCount7d: 1, fpRate: 0.0, mitreId: 'T1499', mitreTactic: 'Impact', suppressionWindow: '5 min', logSource: 'netflow/ingress' },
  { id: 'RULE-014', name: 'Tor Exit Node Traffic', type: 'signature', enabled: true, severity: 'high', condition: 'Src IP matches Tor exit node blocklist', lastTriggered: '07:18:29', triggerCount7d: 234, fpRate: 3.8, mitreId: 'T1090', mitreTactic: 'Defense Evasion', suppressionWindow: '0', logSource: 'netflow/ingress' },
  { id: 'RULE-015', name: 'Anomalous Login Hours', type: 'anomaly', enabled: false, severity: 'low', condition: 'Successful login between 00:00–05:00 local for user', lastTriggered: '2026-03-13 02:14', triggerCount7d: 0, fpRate: 61.2, mitreId: 'T1078', mitreTactic: 'Defense Evasion', suppressionWindow: '0', logSource: 'syslog/auth' },
  { id: 'RULE-016', name: 'SMTP Relay Abuse', type: 'threshold', enabled: true, severity: 'medium', condition: '>500 outbound SMTP messages in 1h from single host', lastTriggered: '2026-03-15 16:44', triggerCount7d: 3, fpRate: 10.5, mitreId: 'T1566', mitreTactic: 'Initial Access', suppressionWindow: '30 min', logSource: 'smtp/logs' },
  { id: 'RULE-017', name: 'Crypto Miner Process', type: 'signature', enabled: true, severity: 'high', condition: 'Process name or cmdline matches known miner signatures', lastTriggered: '2026-03-14 22:17', triggerCount7d: 1, fpRate: 5.0, mitreId: 'T1496', mitreTactic: 'Impact', suppressionWindow: '0', logSource: 'auditd/process' },
  { id: 'RULE-018', name: 'Beaconing Interval Analysis', type: 'behavioral', enabled: true, severity: 'high', condition: 'Periodic outbound connections with <15% jitter over 1h', lastTriggered: '2026-03-15 23:48', triggerCount7d: 6, fpRate: 18.3, mitreId: 'T1071', mitreTactic: 'Command & Control', suppressionWindow: '0', logSource: 'netflow/egress' },
  { id: 'RULE-019', name: 'Log Source Silence Alert', type: 'threshold', enabled: true, severity: 'critical', condition: 'No events from critical log source for >5 min', lastTriggered: '2026-03-15 08:12', triggerCount7d: 2, fpRate: 0.0, mitreId: 'T1562', mitreTactic: 'Defense Evasion', suppressionWindow: '0', logSource: 'system/health' },
  { id: 'RULE-020', name: 'Reverse Shell Pattern', type: 'signature', enabled: true, severity: 'critical', condition: 'Network connection from shell process (bash/sh/cmd) outbound', lastTriggered: '2026-03-15 19:33', triggerCount7d: 4, fpRate: 7.5, mitreId: 'T1059', mitreTactic: 'Execution', suppressionWindow: '0', logSource: 'auditd/network' },
];

const ruleTypeConfig: Record<RuleType, { label: string; classes: string }> = {
  threshold: { label: 'Threshold', classes: 'bg-blue-500/15 text-blue-400 border border-blue-500/20' },
  signature: { label: 'Signature', classes: 'bg-purple-500/15 text-purple-400 border border-purple-500/20' },
  behavioral: { label: 'Behavioral', classes: 'bg-amber-500/15 text-amber-400 border border-amber-500/20' },
  anomaly: { label: 'Anomaly', classes: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' },
};

export default function AlertRulesTable() {
  const [rules, setRules] = useState(initialRules);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<RuleType | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<Severity | 'all'>('all');
  const [enabledFilter, setEnabledFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);

  const handleToggle = (ruleId: string, newVal: boolean) => {
    // Backend integration: PATCH /api/alert-rules/:id { enabled: newVal }
    setRules((prev) => prev.map((r) => r.id === ruleId ? { ...r, enabled: newVal } : r));
    const rule = rules.find((r) => r.id === ruleId);
    toast.success(`${rule?.name} ${newVal ? 'enabled' : 'disabled'}`);
  };

  const handleDelete = (rule: AlertRule) => {
    // Backend integration: DELETE /api/alert-rules/:id
    setRules((prev) => prev.filter((r) => r.id !== rule.id));
    toast.success(`Rule ${rule.id} deleted`);
  };

  const handleEdit = (rule: AlertRule) => {
    setEditingRule(rule);
    setIsModalOpen(true);
  };

  const filtered = rules.filter((r) => {
    if (search && !r.name.toLowerCase().includes(search.toLowerCase()) && !r.id.toLowerCase().includes(search.toLowerCase()) && !r.mitreTactic.toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    if (severityFilter !== 'all' && r.severity !== severityFilter) return false;
    if (enabledFilter === 'enabled' && !r.enabled) return false;
    if (enabledFilter === 'disabled' && r.enabled) return false;
    return true;
  });

  const enabledCount = rules.filter((r) => r.enabled).length;

  return (
    <div className="flex flex-col gap-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Rules', value: rules.length, color: 'text-zinc-200', bg: 'border-zinc-800' },
          { label: 'Active Rules', value: enabledCount, color: 'text-emerald-400', bg: 'border-emerald-500/20 bg-emerald-500/5' },
          { label: 'Disabled Rules', value: rules.length - enabledCount, color: 'text-zinc-500', bg: 'border-zinc-800' },
          { label: 'High FP Rate (>30%)', value: rules.filter((r) => r.fpRate > 30).length, color: 'text-amber-400', bg: 'border-amber-500/20 bg-amber-500/5' },
        ].map((s) => (
          <div key={s.label} className={`bg-zinc-900 border ${s.bg} rounded-xl px-4 py-3`}>
            <p className="text-[11px] font-medium uppercase tracking-widest text-zinc-500">{s.label}</p>
            <p className={`text-2xl font-bold tabular-nums mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search rules, tactics, IDs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30"
          />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)} className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none cursor-pointer">
          <option value="all">All Types</option>
          {(['threshold', 'signature', 'behavioral', 'anomaly'] as RuleType[]).map((t) => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value as any)} className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none cursor-pointer">
          <option value="all">All Severities</option>
          {(['critical', 'high', 'medium', 'low'] as Severity[]).map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <select value={enabledFilter} onChange={(e) => setEnabledFilter(e.target.value as any)} className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none cursor-pointer">
          <option value="all">All States</option>
          <option value="enabled">Enabled Only</option>
          <option value="disabled">Disabled Only</option>
        </select>
        <div className="ml-auto">
          <button
            onClick={() => { setEditingRule(null); setIsModalOpen(true); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-lg transition-colors active:scale-95"
          >
            <Icon name="PlusIcon" size={13} />
            Create Rule
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800">
                {['Rule ID', 'Name', 'Type', 'Severity', 'Condition', 'Log Source', 'Last Triggered', 'Triggers (7d)', 'FP Rate', 'MITRE', 'Suppression', 'Enabled', 'Actions'].map((h) => (
                  <th key={h} className="px-3 py-3 text-left font-semibold text-zinc-600 uppercase tracking-widest text-[10px] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((rule, idx) => (
                <tr
                  key={rule.id}
                  className={`border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors group ${idx % 2 !== 0 ? 'bg-zinc-900/30' : ''} ${!rule.enabled ? 'opacity-60' : ''}`}
                >
                  <td className="px-3 py-3 font-mono text-zinc-500 whitespace-nowrap">{rule.id}</td>
                  <td className="px-3 py-3 max-w-xs">
                    <p className="text-zinc-200 font-medium truncate">{rule.name}</p>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${ruleTypeConfig[rule.type].classes}`}>
                      {ruleTypeConfig[rule.type].label}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap"><SeverityBadge severity={rule.severity} /></td>
                  <td className="px-3 py-3 max-w-xs">
                    <p className="text-zinc-500 truncate font-mono text-[11px]">{rule.condition}</p>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="font-mono text-[11px] text-zinc-600">{rule.logSource}</span>
                  </td>
                  <td className="px-3 py-3 font-mono text-zinc-500 whitespace-nowrap text-[11px]">{rule.lastTriggered}</td>
                  <td className="px-3 py-3 font-mono text-zinc-300 whitespace-nowrap tabular-nums font-semibold">{rule.triggerCount7d.toLocaleString()}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`font-mono text-xs font-semibold tabular-nums ${rule.fpRate > 30 ? 'text-amber-400' : rule.fpRate > 15 ? 'text-orange-400' : 'text-emerald-400'}`}>
                      {rule.fpRate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="font-mono text-[11px] text-purple-400">{rule.mitreId}</span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="font-mono text-[11px] text-zinc-600">{rule.suppressionWindow || '—'}</span>
                  </td>
                  <td className="px-3 py-3">
                    <Toggle
                      checked={rule.enabled}
                      onChange={(val) => handleToggle(rule.id, val)}
                      size="sm"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="relative group/tip">
                        <button
                          onClick={() => handleEdit(rule)}
                          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-300 transition-colors"
                          aria-label="Edit rule"
                        >
                          <Icon name="PencilIcon" size={13} />
                        </button>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-10">Edit rule</span>
                      </div>
                      <div className="relative group/tip">
                        <button
                          onClick={() => handleDelete(rule)}
                          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-red-400 transition-colors"
                          aria-label="Delete rule — this cannot be undone"
                        >
                          <Icon name="TrashIcon" size={13} />
                        </button>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-10">Delete rule — cannot be undone</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-zinc-800">
          <span className="text-xs text-zinc-600 font-mono">{filtered.length} rule{filtered.length !== 1 ? 's' : ''} shown</span>
        </div>
      </div>

      <RuleFormModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingRule(null); }}
        editingRule={editingRule}
        onSave={(rule) => {
          if (editingRule) {
            setRules((prev) => prev.map((r) => r.id === editingRule.id ? { ...r, ...rule } : r));
            toast.success(`Rule ${editingRule.id} updated`);
          } else {
            const newRule: AlertRule = {
              ...rule,
              id: `RULE-${String(rules.length + 1).padStart(3, '0')}`,
              lastTriggered: '—',
              triggerCount7d: 0,
              fpRate: 0,
            } as AlertRule;
            setRules((prev) => [newRule, ...prev]);
            toast.success(`Rule ${newRule.id} created`);
          }
          setIsModalOpen(false);
          setEditingRule(null);
        }}
      />
    </div>
  );
}